import React, { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../components/AppIcon';
import PerformanceInsightsPanel from './components/PerformanceInsightsPanel';
import ExternalIntegrationsPanel from './components/ExternalIntegrationsPanel';
import AutomationPotentialPanel from './components/AutomationPotentialPanel';
import LearningRecommendationsPanel from './components/LearningRecommendationsPanel';

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  avatar: string;
  overallScore: number;
  goalScore: number;
  collaborationScore: number;
  productivityScore: number;
  communicationScore: number;
  innovationScore: number;
  leadershipScore: number;
  goalsCompleted: number;
  totalGoals: number;
  checkIns: number;
  recognitions: number;
  reflections: number;
  strengths: string[];
  growthAreas: string[];
}

const employees: Employee[] = [
  {
    id: 'emp_001',
    name: 'Sarah Johnson',
    position: 'Senior Product Designer',
    department: 'Design',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
    overallScore: 84,
    goalScore: 88,
    collaborationScore: 82,
    productivityScore: 85,
    communicationScore: 78,
    innovationScore: 90,
    leadershipScore: 70,
    goalsCompleted: 9,
    totalGoals: 10,
    checkIns: 11,
    recognitions: 16,
    reflections: 10,
    strengths: ['User Research', 'Visual Storytelling', 'Rapid Prototyping'],
    growthAreas: ['Data-Driven Design', 'Stakeholder Management', 'Design Systems'],
  },
  {
    id: 'emp_002',
    name: 'Mike Chen',
    position: 'Senior Software Engineer',
    department: 'Engineering',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1584b53a9-1762273471611.png',
    overallScore: 82,
    goalScore: 85,
    collaborationScore: 75,
    productivityScore: 90,
    communicationScore: 68,
    innovationScore: 80,
    leadershipScore: 65,
    goalsCompleted: 8,
    totalGoals: 10,
    checkIns: 10,
    recognitions: 12,
    reflections: 9,
    strengths: ['System Architecture', 'Code Quality', 'Performance Optimization'],
    growthAreas: ['Cross-team Communication', 'Mentoring', 'Product Thinking'],
  },
  {
    id: 'emp_003',
    name: 'Lisa Wang',
    position: 'Data Analyst',
    department: 'Analytics',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_129b1ff05-1762273604073.png',
    overallScore: 78,
    goalScore: 80,
    collaborationScore: 72,
    productivityScore: 82,
    communicationScore: 75,
    innovationScore: 68,
    leadershipScore: 60,
    goalsCompleted: 7,
    totalGoals: 10,
    checkIns: 9,
    recognitions: 8,
    reflections: 8,
    strengths: ['SQL & Data Modeling', 'Dashboard Design', 'Statistical Analysis'],
    growthAreas: ['Machine Learning', 'Business Storytelling', 'Strategic Influence'],
  },
  {
    id: 'emp_004',
    name: 'Robert Taylor',
    position: 'Marketing Manager',
    department: 'Marketing',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d5624b8c-1762274875860.png',
    overallScore: 80,
    goalScore: 82,
    collaborationScore: 85,
    productivityScore: 78,
    communicationScore: 88,
    innovationScore: 72,
    leadershipScore: 80,
    goalsCompleted: 8,
    totalGoals: 10,
    checkIns: 11,
    recognitions: 14,
    reflections: 9,
    strengths: ['Campaign Strategy', 'Team Leadership', 'Client Relations'],
    growthAreas: ['Marketing Automation', 'Data Analytics', 'AI-Powered Marketing'],
  },
  {
    id: 'emp_005',
    name: 'Emily Chen',
    position: 'UX Researcher',
    department: 'Design',
    avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_129b1ff05-1762273604073.png',
    overallScore: 76,
    goalScore: 78,
    collaborationScore: 80,
    productivityScore: 74,
    communicationScore: 82,
    innovationScore: 70,
    leadershipScore: 62,
    goalsCompleted: 7,
    totalGoals: 10,
    checkIns: 10,
    recognitions: 10,
    reflections: 8,
    strengths: ['User Interviews', 'Usability Testing', 'Insight Synthesis'],
    growthAreas: ['Quantitative Research', 'AI Research Tools', 'Strategic Roadmapping'],
  },
];

const AIImpactDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [automationPct, setAutomationPct] = useState<number | null>(null);

  const filteredEmployees = employees?.filter(
    (e) =>
      e?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      e?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      e?.position?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-500/10 via-indigo-500/10 to-cyan-500/10 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <AppIcon name="LayoutDashboard" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{t('AI Impact Analysis')}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t('AI-powered performance insights, automation analysis & growth pathways')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
              <AppIcon name="Cpu" size={13} className="text-violet-500" />
              <span>{t('Powered by AI Analysis')}</span>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            {[
              { label: 'Employees Tracked', value: employees?.length, icon: 'Users' as const, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Avg Performance', value: `${Math.round(employees?.reduce((a, e) => a + e?.overallScore, 0) / employees?.length)}/100`, icon: 'TrendingUp' as const, color: 'text-violet-600', bg: 'bg-violet-50' },
              { label: 'Integrations Active', value: 3, icon: 'Plug' as const, color: 'text-cyan-600', bg: 'bg-cyan-50' },
              { label: 'AI Analyses Ready', value: 'On Demand', icon: 'Sparkles' as const, color: 'text-amber-600', bg: 'bg-amber-50' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
                <div className={`w-10 h-10 ${stat?.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <AppIcon name={stat?.icon} size={18} className={stat?.color} />
                </div>
                <div>
                  <div className="text-xl font-bold text-foreground">{stat?.value}</div>
                  <div className="text-xs text-muted-foreground">{stat?.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Employee Selector */}
        <div className="bg-card border border-border rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <AppIcon name="UserSearch" size={16} />
              Select Employee to Analyze
            </h3>
            {selectedEmployee && (
              <button
                onClick={() => { setSelectedEmployee(null); setAutomationPct(null); }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <AppIcon name="X" size={12} /> Clear Selection
              </button>
            )}
          </div>
          <div className="mb-4 max-w-sm">
            <div className="relative">
              <AppIcon name="Search" size={16} className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, department, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background ltr:pl-9 ltr:pr-3 rtl:pr-9 rtl:pl-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {filteredEmployees?.map((emp) => (
              <button
                key={emp?.id}
                onClick={() => { setSelectedEmployee(emp); setAutomationPct(null); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                  selectedEmployee?.id === emp?.id
                    ? 'border-violet-500 bg-violet-50 shadow-sm'
                    : 'border-border hover:border-violet-300 hover:bg-muted/30'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 overflow-hidden">
                  {emp?.avatar ? (
                    <img src={emp?.avatar} alt={`${emp?.name} profile`} className="w-full h-full object-cover" />
                  ) : (
                    emp?.name?.charAt(0)
                  )}
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-foreground">{emp?.name}</div>
                  <div className="text-xs text-muted-foreground">{emp?.position}</div>
                  <div className="text-xs text-muted-foreground">{emp?.department}</div>
                </div>
                {selectedEmployee?.id === emp?.id && (
                  <AppIcon name="CheckCircle2" size={16} className="text-violet-600 ml-1" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-12 gap-5">
          {/* Row 1: Performance + External Integrations */}
          <div className="col-span-7">
            <PerformanceInsightsPanel employee={selectedEmployee} />
          </div>
          <div className="col-span-5">
            <ExternalIntegrationsPanel />
          </div>

          {/* Row 2: Automation + Learning */}
          <div className="col-span-6">
            <AutomationPotentialPanel
              employee={selectedEmployee}
              onAnalysisComplete={(pct) => setAutomationPct(pct)}
            />
          </div>
          <div className="col-span-6">
            <LearningRecommendationsPanel
              employee={selectedEmployee}
              automationPercentage={automationPct}
            />
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg px-4 py-3">
          <AppIcon name="Info" size={13} />
          <span>
            Performance data is sourced from the Performance Snapshot module. External tool data uses mock integrations.
            AI analyses are generated on-demand and are for development planning purposes only.
          </span>
        </div>
      </div>
    </div>
  );
};

export default AIImpactDashboard;
